import React from 'react';
import {Checkbox, Select, Button, SpaceBetween} from '@amzn/awsui-components-react/polaris';
import {
    MULTI_ALLOCATION_DATA_SOURCE_ACTIONS
} from 'src/services/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourcesReducer';
import CONSTANTS from 'src/utils/constants';
import TPEAction from 'src/models/common/TPEAction';
import ServiceCollection from 'src/services/ServiceCollection';
import CommonDataSourceOperations from 'src/services/common/CommonDataSourceOperations';
import Placeholder from 'src/models/tp-allocation/Placeholder';
import CancelEditing from "src/components/calculation-builder/data-sources/CancelEditing";
import {
    MultidimensionalAllocationDataSourceContext
} from "src/services/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourcesContext";
import {isCommonBalancePoolMdaEnabled} from 'src/services/FeatureFlagsService';
import ErrorUtils from 'src/utils/ErrorUtils';
import type {CommonBalancePool} from 'src/models/multidimensional-allocation/CommonBalancePool';
import { findPickerSheet, normaliseSegment } from 'src/services/multidimensional-allocation/data-sources/ratioSheetOptions';

type ComponentProps = {
    recordBeingEdited: {
        entityId: string,
        description?: string,
        drillDownCOASelected?: string,
        isEditing?: boolean,
        balancePullInProgress?: boolean,
        datasource?: string,
        period?: string,
        customPeriod?: string,
        fxDate?: string,
        fxType?: string,
        allocationSegment?: string,
        allocationSheetId?: string,
        poolId?: string,
        poolColumnId?: string,
        isNewRecord?: boolean,
        ratioSheetKind?: string,
        currency?: string,
        errors?: Map<string, string>,
        dataKeyInput?: {
            selectedCompanies: string,
            selectedAccounts: string,
            selectedCOA: Map<string, string>,
        }
    } | undefined,
    sevenSegmentLOV: Map<string, string[]>,
    allRecordDescriptions: string[],
    originalRecordDescription: string,
    coaTypes: string[],
    isSaving: boolean,
    saveButtonLabel: string,
    dispatch: React.Dispatch<TPEAction>,
    services: ServiceCollection,
    onRecordNeedsSaving: (x: string) => void,
    /** Ref populated on render with saveRecord; called by the container's lock-bar-Save path. */
    saveRecordRef?: React.MutableRefObject<(() => boolean) | null>,
}

/**
 * MultidimensionalAllocationDataSourceSelectCOA component implementation
 *
 * Renders the COA selection interface with validation and state management
 *
 * @param {ComponentProps} props - Component properties
 * @returns {JSX.Element} The rendered component
 */
export default function MultidimensionalAllocationDataSourceSelectCOA(props: ComponentProps) {
    const {
        recordBeingEdited, sevenSegmentLOV, allRecordDescriptions, coaTypes, dispatch,
        isSaving, saveButtonLabel, services, onRecordNeedsSaving, saveRecordRef
    } = props;

    const {multidimensionalAllocationDataSourcesState} = React.useContext(MultidimensionalAllocationDataSourceContext);
    const {allocationSheets, customCoaReference = new Map<string, string>()} = multidimensionalAllocationDataSourcesState;

    const {
        dataKeyInput = {selectedCompanies: '', selectedAccounts: '', selectedCOA: new Map<string, string>()},
        isEditing = false,
        allocationSegment = ''
    } = recordBeingEdited || {};

    const {selectedCompanies, selectedAccounts, selectedCOA} = dataKeyInput;

    const isPoolRow = isCommonBalancePoolMdaEnabled()
        && recordBeingEdited?.datasource === CONSTANTS.DATA_SOURCE_TYPES.COMMON_BALANCE_POOL;

    // Hook is unconditional (rules of hooks); the service only fetches when query is non-empty.
    const [poolsResponse, poolsLoading, poolsError] = services.commonBalancePoolService
        .listCommonBalancePools(isPoolRow ? `mda-ds-${recordBeingEdited?.entityId}` : '', undefined, 'ACTIVE');
    const [selectedPool, setSelectedPool] = React.useState<CommonBalancePool | null>(null);
    const [poolLoading, setPoolLoading] = React.useState(false);
    const [poolLoadError, setPoolLoadError] = React.useState('');

    React.useEffect(() => {
        const poolId = recordBeingEdited?.poolId;
        // Drop the previous pool's definition first, so its columns cannot be picked while the new one loads.
        setSelectedPool(null);
        setPoolLoadError('');
        if (!isPoolRow || !poolId) {
            setPoolLoading(false);
            return;
        }
        let active = true;
        setPoolLoading(true);
        // Version 0 is the live definition, the one save and execution validate against (not an open draft).
        services.commonBalancePoolService.getCommonBalancePool(poolId, 0)
            .then(pool => {
                if (!active) return;
                // The service resolves null for an unknown id; the user needs to see why no column is offered.
                if (pool) setSelectedPool(pool); else setPoolLoadError(`No common balance pool found with id: ${poolId}`);
            })
            .catch((ex) => { if (active) setPoolLoadError(ErrorUtils.getMessage(ex)); })
            .finally(() => { if (active) setPoolLoading(false); });
        return () => { active = false; };
    }, [isPoolRow, recordBeingEdited?.poolId]);

    // A persisted pool row keeps its grain, so only pools of the saved pool's grain are offered.
    const listGrain = recordBeingEdited?.isNewRecord ? undefined : selectedPool?.grain;
    // A grain with no COA segment (LEGAL_ENTITY) has no MDA allocation sheet, so such a pool could never be saved.
    const listedPools = (poolsResponse?.pools ?? [])
        .filter(pool => pool.grain in CONSTANTS.COA_SEGMENT_MAPPING)
        .filter(pool => !listGrain || pool.grain === listGrain);
    // The list holds ACTIVE pools only; a saved pool that has since left ACTIVE is still the row's pool.
    const pools = selectedPool && !listedPools.some(pool => pool.poolId === selectedPool.poolId)
        ? [selectedPool, ...listedPools]
        : listedPools;
    const poolOptions = pools.map(pool => ({label: pool.poolName ?? pool.poolId, value: pool.poolId}));
    // Only a summable column can back a data source: CALC with a blank, NUMBER or CURRENCY data type
    // (the rule the WebAPI save validator and the execution resolver both apply).
    const columnOptions = [...(selectedPool?.columns ?? [])]
        .filter(column => column.kind === 'CALC' && (!column.dataType || column.dataType === 'NUMBER' || column.dataType === 'CURRENCY'))
        .sort((a, b) => (a.columnOrder ?? Number.MAX_SAFE_INTEGER) - (b.columnOrder ?? Number.MAX_SAFE_INTEGER))
        .map(column => ({label: column.displayName, value: column.columnId}));

    // A grain outside COA_SEGMENT_MAPPING (LEGAL_ENTITY) has no UI label and no sheet segment (MDA_SEGMENTS), so it is
    // kept verbatim: the sheet filter upper-cases either form and matches nothing, and the read-only Select shows the grain.
    const segmentForGrain = (grain: string): string => {
        const mapped = (CONSTANTS.COA_SEGMENT_MAPPING as Record<string, {UI: string}>)[grain];
        return mapped ? mapped.UI : grain;
    };

    // Same normalisation as FILTER_ALLOCATION_SHEETS_BY_SEGMENT, so 'Cost Center' and 'COST_CENTER' compare equal.
    const normSegment = (segment?: string): string => (segment ?? '').toUpperCase().replace(/\s+/g, '_');

    // LG Code (segment 8) is only valid for Secondary Ledger datasources; grey it out for all
    // others. Mirrors the calculation-builder SelectCOA behavior.
    const isLgCodeDisabledForRow = (coaType: string): boolean => {
        return coaType === CONSTANTS.COA_SEGMENT_MAPPING.LG_CODE.UI
            && recordBeingEdited?.datasource !== CONSTANTS.DATA_SOURCE_TYPES.SECONDARY_LEDGER;
    };

    const coaTypeOptions = coaTypes
        .filter((coaType: string) => (coaType != CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI && coaType != CONSTANTS.COA_SEGMENT_MAPPING.GL_ACCOUNT.UI))
        .map((coaType: string) => ({label: coaType, value: coaType, disabled: isLgCodeDisabledForRow(coaType)}));


    const isCOASelected = (key: string) => {
        return selectedCOA.has(key);
    }

    const toggleCOA = (key: string) => {
        if (isCOASelected(key)) {
            dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.REMOVE_DATA_KEY_COA_INPUT.withPayload(key));
        } else {
            dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_DATA_KEY_COA_INPUT.withPayload({key: key, value: ''}));
        }
    }

    // Returns true iff validation passed and a save was queued; false when validation errors
    // were dispatched to the reducer and rendered inline.
    const saveRecord = (): boolean => {
        const errorMap = services.dataSourcesService.validateDataSourceDescription(recordBeingEdited?.description, allRecordDescriptions);

        // Validate allocation sheet selection
        if (!recordBeingEdited?.allocationSheetId) {
            errorMap.set('allocationSheetId', 'Required');
        }

        if (!recordBeingEdited?.allocationSegment) {
            errorMap.set('allocationSegment', 'Segment to allocate on is required');
        }

        if (isPoolRow) {
            if (!recordBeingEdited?.poolId) {
                errorMap.set('poolId', 'Required');
            } else if (!selectedPool) {
                // The column and sheet/grain checks below need the pool definition; a failed or pending load blocks Save.
                errorMap.set('poolId', poolLoadError || 'Pool definition is still loading');
            }
            if (!recordBeingEdited?.poolColumnId) {
                errorMap.set('poolColumnId', 'Required');
            } else if (selectedPool && !columnOptions.some(option => option.value === recordBeingEdited.poolColumnId)) {
                // The picker shows blank for a saved column the loaded pool no longer offers; refuse it here too.
                errorMap.set('poolColumnId', 'Required');
            }
            // The list holds uploaded and derived sheets, whose ids are unique only within their own kind.
            const sheet = findPickerSheet(allocationSheets, recordBeingEdited?.allocationSheetId,
                recordBeingEdited?.ratioSheetKind);
            if (selectedPool && sheet && normSegment(sheet.segmentType) !== normSegment(segmentForGrain(selectedPool.grain))) {
                errorMap.set('allocationSheetId', 'Allocation sheet segment does not match the pool grain');
            }
        }

        // Validate inputs if saved without pulling balance (a pool row has no Company/GL inputs)
        if (!recordBeingEdited?.balancePullInProgress && !isPoolRow) {
            dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.REMOVE_UNUSED_DATA_KEY_COA_INPUTS);

            // This validation will be required for pull balance.
            const inputsErrorMap = CommonDataSourceOperations.validateInputsForPullBalance(recordBeingEdited, false);
            CommonDataSourceOperations.validateCOAInput(CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI, selectedCompanies, sevenSegmentLOV.get(CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI) || [], customCoaReference, inputsErrorMap, new Map<string, Placeholder[]>);
            CommonDataSourceOperations.validateCOAInput(CONSTANTS.COA_SEGMENT_MAPPING.GL_ACCOUNT.UI, selectedAccounts, sevenSegmentLOV.get(CONSTANTS.COA_SEGMENT_MAPPING.GL_ACCOUNT.UI) || [], customCoaReference, inputsErrorMap, new Map<string, Placeholder[]>);
            selectedCOA.forEach((value, key) => CommonDataSourceOperations.validateCOAInput(key, value, sevenSegmentLOV.get(key) || [], customCoaReference, inputsErrorMap, new Map<string, Placeholder[]>));

            if (inputsErrorMap.size > 0) {
                inputsErrorMap.forEach((value: string, key: string) => errorMap.set(key, value));
            }
        }

        if (errorMap.size > 0) {
            dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_ERRORS.withPayload({
                recordID: recordBeingEdited?.entityId,
                value: errorMap
            }));
            return false;
        }
        dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.CLEAR_ERRORS.withPayload({recordID: recordBeingEdited?.entityId}));
        dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_FX_INPUTS);
        onRecordNeedsSaving(new Date().toISOString());
        return true;
    }

    // Expose saveRecord to the container so lock-bar Save can trigger the same validate-and-save.
    // Cleared on unmount so a subsequent row rendering a different selector sees a null ref.
    if (saveRecordRef) {
        saveRecordRef.current = saveRecord;
    }
    React.useEffect(() => () => {
        if (saveRecordRef) { saveRecordRef.current = null; }
    }, [saveRecordRef]);

    // A new action per dispatch: withPayload mutates the shared UPDATE_RECORD_FIELD instance, so several
    // dispatches in one handler would otherwise all reduce with the last payload.
    const updateField = (fieldName: string, value: any) =>
        dispatch(new TPEAction(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.UPDATE_RECORD_FIELD.type).withPayload({fieldName, value}));

    const handleCOADropdownChange = ({detail}: any) => {
        const selectedCOAType = detail.selectedOption?.value;
        if (selectedCOAType) {

            // Check if current allocation sheet is compatible with new segment
            if (recordBeingEdited?.allocationSheetId) {
                const currentSheet = findPickerSheet(allocationSheets, recordBeingEdited.allocationSheetId,
                    recordBeingEdited.ratioSheetKind);
                // segmentType is the enum name (COST_CENTER) and the dropdown gives the label (Cost Center).
                // A sheet not in the list (still loading, or missing) can't be checked, so any real segment change clears it.
                const sheetDoesNotFit = currentSheet
                    ? normaliseSegment(currentSheet.segmentType) !== normaliseSegment(selectedCOAType)
                    : normaliseSegment(allocationSegment) !== normaliseSegment(selectedCOAType);
                if (sheetDoesNotFit) {
                    // Clear incompatible allocation sheet selection
                    updateField('allocationSheetId', '');
                    updateField('allocationSheetVersion', undefined);
                    updateField('ratioSheetKind', undefined);
                    // Clear any existing allocation sheet errors
                    if (recordBeingEdited?.errors?.has('allocationSheetId')) {
                        const updatedErrors = new Map(recordBeingEdited.errors);
                        updatedErrors.delete('allocationSheetId');
                        dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_ERRORS.withPayload({
                            recordID: recordBeingEdited.entityId,
                            value: updatedErrors.size > 0 ? updatedErrors : undefined
                        }));
                    }
                }
            }

            updateField('allocationSegment', selectedCOAType);

            // Filter allocation sheets based on the selected segment
            dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.FILTER_ALLOCATION_SHEETS_BY_SEGMENT.withPayload(selectedCOAType));

            if (isCOASelected(selectedCOAType)) {
                dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.REMOVE_DATA_KEY_COA_INPUT.withPayload(selectedCOAType));
            }
        }
    }

    const clearErrors = (keys: string[]) => {
        if (!recordBeingEdited?.errors || !keys.some(key => recordBeingEdited.errors!.has(key))) {
            return;
        }
        const updatedErrors = new Map(recordBeingEdited.errors);
        keys.forEach(key => updatedErrors.delete(key));
        dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_ERRORS.withPayload({
            recordID: recordBeingEdited.entityId,
            value: updatedErrors.size > 0 ? updatedErrors : undefined
        }));
    };

    const handlePoolChange = ({detail}: any) => {
        const poolId = detail.selectedOption?.value;
        if (!poolId) {
            return;
        }
        const pool = pools.find(candidate => candidate.poolId === poolId);
        const segment = pool ? segmentForGrain(pool.grain) : '';
        updateField('poolId', poolId);
        updateField('poolColumnId', undefined);
        updateField('allocationSheetId', '');
        updateField('allocationSheetVersion', undefined);
        // No shared sheet filter here: the MagicBox builds a pool row's sheet list from its own segment.
        updateField('allocationSegment', segment);
        clearErrors(['poolId', 'poolColumnId', 'allocationSheetId', 'allocationSegment']);
    };

    const handleColumnChange = ({detail}: any) => {
        updateField('poolColumnId', detail.selectedOption?.value);
        clearErrors(['poolColumnId']);
    };

    return (
        <div className="dataKeyNav">
            {/* A pool row pulls no GL balance, so these checkboxes select nothing: the heading would be
                telling the user to configure a pull that never happens. */}
            {!isPoolRow &&
                <>
                    <h2>Select COAs to pull GL Balance</h2>
                    <div className="coaContainer">
                        {coaTypes.filter((coaType: string) => (coaType != CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI && coaType != CONSTANTS.COA_SEGMENT_MAPPING.GL_ACCOUNT.UI))
                            .map((coaType: string) => <div key={coaType} className="coaItemContainer">
                                <Checkbox className={'coaItem ' + coaType.replace(' ', '_') + 'COASelect'}
                                          disabled={!isEditing || (allocationSegment === coaType) || isLgCodeDisabledForRow(coaType)}
                                          checked={isCOASelected(coaType)}
                                          onChange={() => toggleCOA(coaType)}>{coaType}</Checkbox>
                            </div>)}
                    </div>
                </>
            }
            {isPoolRow &&
                <div className="dataKeyNav">
                    <h2>Common Balance Pool</h2>
                    <Select className="cbpPoolSelect" ariaLabel="Common Balance Pool"
                            selectedOption={poolOptions.find(option => option.value === recordBeingEdited?.poolId) ?? null}
                            onChange={handlePoolChange}
                            options={poolOptions}
                            placeholder="Select a pool"
                            empty="No active pools"
                            statusType={poolsLoading ? 'loading' : poolsError ? 'error' : 'finished'}
                            errorText={poolsError}
                            disabled={!isEditing}
                            invalid={recordBeingEdited?.errors?.has('poolId')}
                    />
                </div>
            }
            {isPoolRow &&
                <div className="dataKeyNav">
                    <h2>Pool column</h2>
                    <Select className="cbpColumnSelect" ariaLabel="Pool column"
                            selectedOption={columnOptions.find(option => option.value === recordBeingEdited?.poolColumnId) ?? null}
                            onChange={handleColumnChange}
                            options={columnOptions}
                            placeholder="Select a column"
                            statusType={poolLoading ? 'loading' : poolLoadError ? 'error' : 'finished'}
                            errorText={poolLoadError}
                            disabled={!isEditing || !recordBeingEdited?.poolId}
                            invalid={recordBeingEdited?.errors?.has('poolColumnId')}
                    />
                </div>
            }
            {
                <div className="dataKeyNav">
                    <h2>Segment to allocate on</h2>
                    <Select
                        selectedOption={recordBeingEdited?.allocationSegment ?
                            {label: recordBeingEdited.allocationSegment, value: recordBeingEdited.allocationSegment} :
                            null}
                        onChange={handleCOADropdownChange}
                        options={coaTypeOptions}
                        placeholder="Select segment to add"
                        disabled={!isEditing || isPoolRow}
                        invalid={recordBeingEdited?.errors && recordBeingEdited.errors.has('allocationSegment')}
                    />
                </div>
            }
            {isEditing &&
                <div>
                    <SpaceBetween className="actionButtonsContainer" direction="horizontal" size="xs">
                        <CancelEditing
                            onCancelPostProcess={() => dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.CANCEL_EDITING)}/>
                        <Button className="smallPrimaryButton saveDataSourceBtn" loading={isSaving}
                                onClick={saveRecord}>{isSaving ? "Saving..." : saveButtonLabel}</Button>
                    </SpaceBetween>
                </div>
            }
        </div>
    )
}
